import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, BookOpenIcon, DocumentTextIcon, SparklesIcon, TagIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getResources, getFeaturedResources } from '../lib/db';
import { Link } from 'react-router-dom';
import Tooltip from '../components/Tooltip';
import FavoriteButton from '../components/common/FavoriteButton';

const ContentLibrary = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [playbooks, setPlaybooks] = useState([]);
  const [featuredPlaybooks, setFeaturedPlaybooks] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ tags: [] });
  const [availableFilters, setAvailableFilters] = useState({ tags: [] });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all resources
        const resources = await getResources();
        setPlaybooks(resources);

        // Fetch featured resources
        const featured = await getFeaturedResources(3);
        setFeaturedPlaybooks(featured);

        // Extract unique categories
        const uniqueCategories = ["All", ...new Set(resources.map(resource => resource.category))];
        setCategories(uniqueCategories);

        // Extract unique tags
        const allTags = resources.flatMap(r => Array.isArray(r.type) ? r.type : (typeof r.type === 'string' ? r.type.split(',').map(t => t.trim()) : []));
        const uniqueTags = Array.from(new Set(allTags.filter(Boolean)));
        setAvailableFilters({ tags: uniqueTags });
      } catch (error) {
        console.error("Error fetching resources:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchFilteredResources = async () => {
      setLoading(true);
      try {
        const filteredResources = await getResources(searchTerm, selectedCategory, filters);
        setPlaybooks(filteredResources);
      } catch (error) {
        console.error("Error fetching filtered resources:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredResources();
  }, [searchTerm, selectedCategory, filters]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const toggleFilter = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(v => v !== value)
        : [...prev[type], value]
    }));
  };

  const clearFilters = () => {
    setFilters({ tags: [] });
    setSelectedCategory("All");
    setSearchTerm("");
  };

  const FilterButton = ({ type, value, icon: Icon }) => (
    <button
      onClick={() => {
        if (value === 'All') {
          setFilters(prev => ({ ...prev, [type]: [] }));
        } else {
          toggleFilter(type, value);
        }
      }}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        (value === 'All' && filters[type].length === 0) || (value !== 'All' && filters[type].includes(value))
          ? 'bg-primary text-white'
          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
      }`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {value}
    </button>
  );

  // Helper to get icon by resource_type
  const getResourceTypeIcon = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'playbook':
        return BookOpenIcon;
      case 'guide':
        return DocumentTextIcon;
      default:
        return BookOpenIcon;
    }
  };

  return (
    <div className="w-full mx-auto max-w-7xl">
      {/* Enhanced Header Section */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-6 mb-8 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-6 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Content Library</h1>
            <p className="text-gray-300 text-base">Access exclusive playbooks, guides, and resources</p>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            <input
              type="text"
              placeholder="Search resources..."
              className="w-full py-4 pl-12 pr-4 bg-gray-900 border-2 border-gray-600 rounded-xl text-white text-base focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/50 transition-all"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-4 rounded-xl text-base font-semibold transition-all flex items-center gap-3 ${
              showFilters 
                ? 'bg-primary text-white shadow-lg ring-2 ring-primary/50' 
                : 'bg-gray-700 text-gray-200 hover:bg-gray-600 hover:text-white border-2 border-gray-600'
            }`}
          >
            <FunnelIcon className="h-6 w-6" />
            <span className="hidden sm:inline">Filters</span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-800 rounded-xl p-4 mb-6 space-y-4 sticky top-0 z-20 shadow-lg border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-white">Filters</h2>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-2"
            >
              Clear all
            </button>
          </div>

          {/* Tags */}
          {availableFilters.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {availableFilters.tags.map(tag => (
                  <FilterButton
                    key={tag}
                    type="tags"
                    value={tag}
                    icon={TagIcon}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active filters */}
      {(filters.tags.length > 0 || selectedCategory !== "All") && (
        <div className="flex flex-wrap gap-2 mb-6">
          {selectedCategory !== "All" && (
            <span className="flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
              {selectedCategory}
              <button
                onClick={() => setSelectedCategory("All")}
                className="hover:text-primary-dark"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          )}
          {filters.tags.map(tag => (
            <span key={tag} className="flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
              <TagIcon className="h-4 w-4" />
              {tag}
              <button
                onClick={() => toggleFilter('tags', tag)}
                className="hover:text-primary-dark"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Featured Playbooks Section */}
      <section className="mb-8 sm:mb-12">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center">
            <SparklesIcon className="h-6 w-6 text-primary-light mr-2" />
            Latest Resource Drops
          </h2>
        </div>
        {loading && featuredPlaybooks.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-900 rounded-lg overflow-hidden shadow-lg animate-pulse h-80">
                <div className="w-full h-40 bg-gray-800" />
                <div className="p-4 sm:p-5">
                  <div className="h-4 bg-gray-800 w-1/4 mb-2 rounded" />
                  <div className="h-6 bg-gray-800 w-3/4 mb-2 rounded" />
                  <div className="h-4 bg-gray-800 w-full mb-4 rounded" />
                  <div className="flex justify-between items-center">
                    <div className="h-3 bg-gray-800 w-1/3 rounded" />
                    <div className="h-3 bg-gray-800 w-1/4 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {featuredPlaybooks.map(playbook => (
              playbook.slug ? (
                <Link
                  key={playbook.id}
                  to={`/playbooks/${playbook.slug.trim()}/intro`}
                  className="bg-gray-900 rounded-lg overflow-hidden shadow-lg transition-transform duration-200 hover:-translate-y-1 ring-1 ring-primary/20 hover:ring-primary/40 flex flex-col justify-between group h-full min-h-[380px] relative"
                >
                  <div className="absolute top-3 right-3 z-10">
                    <FavoriteButton
                      itemId={playbook.id}
                      itemType="resource"
                      size="md"
                    />
                  </div>
                  <img src={playbook.image} alt={playbook.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-5 flex flex-col flex-1 justify-between gap-2">
                    {/* Resource type and icon */}
                    <div className="flex items-center mb-2">
                      {(() => {
                        const Icon = getResourceTypeIcon(playbook.resource_type);
                        return <Icon className="h-5 w-5 text-primary-light mr-2" />;
                      })()}
                      <span className="text-xs font-semibold text-primary-light capitalize">{playbook.resource_type || 'How To Guide'}</span>
                    </div>
                    {/* Title area with fixed height for alignment */}
                    <div className="h-[72px] flex items-start mb-3">
                      <h3 className="text-base sm:text-lg font-semibold text-white line-clamp-3">{playbook.title}</h3>
                    </div>
                    <Tooltip content={playbook.description}>
                      <p className="text-sm text-gray-400 mb-4 line-clamp-3">{playbook.description}</p>
                    </Tooltip>
                    <div className="flex-1" />
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-800 mt-auto min-h-[40px] flex-col gap-1 sm:flex-row sm:gap-0">
                      <span>{playbook.author}</span>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-h-[20px]">
                        {(() => {
                          let tags = [];
                          if (Array.isArray(playbook.type)) {
                            tags = playbook.type;
                          } else if (typeof playbook.type === 'string' && playbook.type.includes(',')) {
                            tags = playbook.type.split(',').map(tag => tag.trim());
                          } else if (playbook.type) {
                            tags = [playbook.type];
                          }
                          const displayTags = tags.slice(0, 2);
                          const extraCount = tags.length > 2 ? tags.length - 2 : 0;
                          return (
                            <>
                              {displayTags.map(tag => (
                                <span key={tag} className="text-primary-light text-xs font-semibold mr-1">#{tag}</span>
                              ))}
                              {extraCount > 0 && (
                                <span className="text-gray-400 text-xs font-semibold">+{extraCount} more</span>
                              )}
                              {/* Add invisible placeholder if less than 2 tags for alignment */}
                              {displayTags.length < 2 && (
                                <span className="invisible">#placeholder</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </Link>
              ) : null
            ))}
          </div>
        )}
      </section>

      {/* All Playbooks Section */}
      <section>
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                selectedCategory === category 
                  ? 'bg-primary border-primary text-white' 
                  : 'bg-gray-800 border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
              onClick={() => handleCategorySelect(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {loading && playbooks.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-900 rounded-lg overflow-hidden shadow-lg animate-pulse h-72">
                <div className="w-full h-32 bg-gray-800" />
                <div className="p-4 sm:p-5">
                  <div className="h-4 bg-gray-800 w-1/4 mb-2 rounded" />
                  <div className="h-6 bg-gray-800 w-3/4 mb-2 rounded" />
                  <div className="h-4 bg-gray-800 w-full mb-4 rounded" />
                  <div className="flex justify-between items-center">
                    <div className="h-3 bg-gray-800 w-1/3 rounded" />
                    <div className="h-3 bg-gray-800 w-1/4 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : playbooks.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {playbooks.map(playbook => (
              playbook.slug ? (
                <Link
                  key={playbook.id}
                  to={`/playbooks/${playbook.slug.trim()}/intro`}
                  className="bg-gray-900 rounded-lg overflow-hidden shadow-lg transition-transform duration-200 hover:-translate-y-1 ring-1 ring-primary/20 hover:ring-primary/40 flex flex-col justify-between group h-full min-h-[380px] relative"
                >
                  <div className="absolute top-3 right-3 z-10">
                    <FavoriteButton
                      itemId={playbook.id}
                      itemType="resource"
                      size="md"
                    />
                  </div>
                  <img src={playbook.image} alt={playbook.title} className="w-full h-40 object-cover rounded-t-lg" />
                  <div className="p-5 flex flex-col flex-1 justify-between gap-2">
                    {/* Resource type and icon */}
                    <div className="flex items-center mb-2">
                      {(() => {
                        const Icon = getResourceTypeIcon(playbook.resource_type);
                        return <Icon className="h-5 w-5 text-primary-light mr-2" />;
                      })()}
                      <span className="text-xs font-semibold text-primary-light capitalize">{playbook.resource_type || 'How To Guide'}</span>
                    </div>
                    {/* Title area with fixed height for alignment */}
                    <div className="h-[72px] flex items-start mb-3">
                      <h3 className="text-base sm:text-lg font-semibold text-white line-clamp-3">{playbook.title}</h3>
                    </div>
                    <Tooltip content={playbook.description}>
                      <p className="text-sm text-gray-400 mb-4 line-clamp-3">{playbook.description}</p>
                    </Tooltip>
                    <div className="flex-1" />
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-800 mt-auto min-h-[40px] flex-col gap-1 sm:flex-row sm:gap-0">
                      <span>{playbook.author}</span>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-h-[20px]">
                        {(() => {
                          let tags = [];
                          if (Array.isArray(playbook.type)) {
                            tags = playbook.type;
                          } else if (typeof playbook.type === 'string' && playbook.type.includes(',')) {
                            tags = playbook.type.split(',').map(tag => tag.trim());
                          } else if (playbook.type) {
                            tags = [playbook.type];
                          }
                          const displayTags = tags.slice(0, 2);
                          const extraCount = tags.length > 2 ? tags.length - 2 : 0;
                          return (
                            <>
                              {displayTags.map(tag => (
                                <span key={tag} className="text-primary-light text-xs font-semibold mr-1">#{tag}</span>
                              ))}
                              {extraCount > 0 && (
                                <span className="text-gray-400 text-xs font-semibold">+{extraCount} more</span>
                              )}
                              {/* Add invisible placeholder if less than 2 tags for alignment */}
                              {displayTags.length < 2 && (
                                <span className="invisible">#placeholder</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </Link>
              ) : null
            ))}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12 bg-gray-900 rounded-lg">
            <BookOpenIcon className="mx-auto h-12 w-12 text-primary-light mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-white mb-2">No resources found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default ContentLibrary; 